/**
 * Shared feed data hook — business logic extracted from FeedClient.
 * Used by both desktop FeedClient and mobile MobileFeed.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import resolveAvatar from '../../../lib/resolveAvatar';

import { fetchWithAuth } from '../../../lib/fetchWithAuth';
// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Reply = {
  id: string;
  content: string;
  author: {
    id: number;
    username: string;
    profileName?: string;
    profilePicture?: string;
  };
  likes: number;
  dislikes: number;
  liked?: boolean;
  disliked?: boolean;
  createdAt: string;
};

export type FeedPost = {
  id: string;
  author: string;
  authorId: string;
  authorUsername?: string;
  authorAvatar?: string;
  title?: string;
  slug?: string;
  content: string;
  mediaUrl?: string;
  imageUrl?: string;
  aboveContent?: boolean;
  belowContent?: boolean;
  tags?: string[];
  likes: number;
  dislikes: number;
  disliked?: boolean;
  comments: number;
  shares: number;
  timestamp: string;
  liked?: boolean;
  createdAt: string;
  editedByModeratorId?: string;
  editedByModeratorAt?: string;
  moderatorEditReason?: string;
  communitySlug?: string;
  communityName?: string;
  postType?: string;
  isPinned?: boolean;
  isPinnedToFeed?: boolean;
  replies?: Reply[];
};

export type FeedTab = 'all' | 'following' | 'friends' | 'community' | 'tags';
export type SortOption = 'newest' | 'oldest' | 'popular' | 'unpopular' | 'controversial';

// ---------------------------------------------------------------------------
// Normalize likes/dislikes from backend (may be array of {userId} or number)
// ---------------------------------------------------------------------------

/** Ensure likes/dislikes are numbers, not arrays. Apply to raw backend posts. */
export function normalizeLikeCounts<T extends Record<string, any>>(post: T): T {
  return {
    ...post,
    likes: Array.isArray(post.likes) ? post.likes.length : (typeof post.likes === 'number' ? post.likes : 0),
    dislikes: Array.isArray(post.dislikes) ? post.dislikes.length : (typeof post.dislikes === 'number' ? post.dislikes : 0),
  };
}

// ---------------------------------------------------------------------------
// Transform backend post to frontend format
// ---------------------------------------------------------------------------

export function transformBackendPost(post: any): FeedPost {
  const likesCount = Array.isArray(post.likes) ? post.likes.length : (typeof post.likes === 'number' ? post.likes : 0);
  const dislikesCount = Array.isArray(post.dislikes) ? post.dislikes.length : (typeof post.dislikes === 'number' ? post.dislikes : 0);

  const replies = post.replies?.map((comment: any) => ({
    id: comment.id?.toString() || comment.id,
    content: comment.content,
    author: comment.author,
    likes: typeof comment.likes === 'number' ? comment.likes : 0,
    dislikes: typeof comment.dislikes === 'number' ? comment.dislikes : 0,
    liked: comment.liked || false,
    disliked: comment.disliked || false,
    createdAt: comment.createdAt,
  }));

  return {
    id: post.id,
    author: post.authorName || (typeof post.author === 'string' ? post.author : (post.author?.profileName || post.author?.name || post.author?.username)) || 'Unknown',
    authorId: post.authorId || post.author?.id || 'Unknown',
    authorAvatar: resolveAvatar(post.authorAvatar || post.author?.profilePicture || post.author?.image),
    authorUsername: post.authorUsername || post.author?.username,
    slug: post.slug,
    title: post.title,
    content: post.content,
    mediaUrl: post.mediaUrl || post.imageUrl,
    aboveContent: post.aboveContent,
    belowContent: post.belowContent,
    tags: post.tags || [],
    likes: likesCount,
    dislikes: dislikesCount,
    disliked: post.disliked || false,
    comments: post.comments || post._count?.comments || post._count?.replies || 0,
    shares: 0,
    timestamp: post.timestamp || (post.createdAt ? new Date(post.createdAt).toLocaleString() : 'Unknown'),
    liked: post.liked || false,
    createdAt: typeof post.createdAt === 'string' ? post.createdAt : (post.createdAt ? new Date(post.createdAt).toISOString() : new Date().toISOString()),
    editedByModeratorId: post.editedByModeratorId,
    editedByModeratorAt: typeof post.editedByModeratorAt === 'string' ? post.editedByModeratorAt : (post.editedByModeratorAt ? new Date(post.editedByModeratorAt).toISOString() : undefined),
    moderatorEditReason: post.moderatorEditReason,
    communitySlug: post.communitySlug,
    communityName: post.communityName,
    postType: post.postType,
    isPinned: post.isPinned,
    isPinnedToFeed: post.isPinnedToFeed,
    replies,
  };
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

export function sortPosts(posts: FeedPost[], sortBy: SortOption): FeedPost[] {
  const sorted = [...posts];
  switch (sortBy) {
    case 'newest':
      return sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    case 'oldest':
      return sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    case 'popular':
      return sorted.sort((a, b) => b.likes - a.likes);
    case 'unpopular':
      return sorted.sort((a, b) => (b.dislikes || 0) - (a.dislikes || 0));
    case 'controversial':
      return sorted.sort((a, b) => {
        const aC = Math.min(a.likes, a.dislikes || 0);
        const bC = Math.min(b.likes, b.dislikes || 0);
        return bC - aC;
      });
    default:
      return sorted;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useFeedData() {
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [followingPosts, setFollowingPosts] = useState<FeedPost[]>([]);
  const [friendsPosts, setFriendsPosts] = useState<FeedPost[]>([]);
  const [communityPosts, setCommunityPosts] = useState<FeedPost[]>([]);
  const [tagsPosts, setTagsPosts] = useState<FeedPost[]>([]);
  const [activeTab, setActiveTab] = useState<FeedTab>('all');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Fetch all posts on mount
  useEffect(() => {
    (async () => {
      try {
        const response = await fetchWithAuth('/api/posts', { credentials: 'include' });
        if (response.ok && mountedRef.current) {
          const data = await response.json();
          const transformed = (data.items || []).map(transformBackendPost);
          setPosts(transformed);
        }
      } catch { /* silent */ }
      finally { if (mountedRef.current) setIsLoading(false); }
    })();
  }, []);

  // Fetch tab-specific posts
  useEffect(() => {
    (async () => {
      try {
        if (activeTab === 'community') {
          const response = await fetchWithAuth('/api/communities/feed', { credentials: 'include' });
          if (response.ok && mountedRef.current) {
            const data = await response.json();
            setCommunityPosts((data.posts || []).map(transformBackendPost));
          }
        } else if (activeTab === 'following') {
          const response = await fetchWithAuth('/api/posts/following', { credentials: 'include' });
          if (response.ok && mountedRef.current) {
            const data = await response.json();
            setFollowingPosts((data.items || []).map(transformBackendPost));
          }
        } else if (activeTab === 'friends') {
          const response = await fetchWithAuth('/api/posts/friends', { credentials: 'include' });
          if (response.ok && mountedRef.current) {
            const data = await response.json();
            setFriendsPosts((data.items || []).map(transformBackendPost));
          }
        } else if (activeTab === 'tags') {
          const response = await fetchWithAuth('/api/posts/tags', { credentials: 'include' });
          if (response.ok && mountedRef.current) {
            const data = await response.json();
            setTagsPosts((data.items || []).map(transformBackendPost));
          }
        }
      } catch { /* silent */ }
    })();
  }, [activeTab]);

  // Get filtered posts based on active tab
  const getFilteredPosts = useCallback((): FeedPost[] => {
    switch (activeTab) {
      case 'following': return followingPosts;
      case 'friends': return friendsPosts;
      case 'community': return communityPosts;
      case 'tags': return tagsPosts;
      default: return posts;
    }
  }, [activeTab, posts, followingPosts, friendsPosts, communityPosts, tagsPosts]);

  // Get sorted + filtered posts
  const getSortedPosts = useCallback((): FeedPost[] => {
    return sortPosts(getFilteredPosts(), sortBy);
  }, [getFilteredPosts, sortBy]);

  // Refresh feed
  const refreshFeed = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await fetchWithAuth('/api/posts', { credentials: 'include' });
      if (response.ok && mountedRef.current) {
        const data = await response.json();
        setPosts((data.items || []).map(transformBackendPost));
      }
    } catch { /* silent */ }
    finally { if (mountedRef.current) setIsRefreshing(false); }
  }, []);

  // Like post (optimistic update across all post lists)
  const likePost = useCallback(async (postId: string) => {
    const updateLike = (prev: FeedPost[]) =>
      prev.map(p => {
        if (p.id !== postId) return p;
        return { ...p, liked: !p.liked, likes: p.liked ? p.likes - 1 : p.likes + 1 };
      });

    setPosts(updateLike);
    setFollowingPosts(updateLike);
    setFriendsPosts(updateLike);
    setCommunityPosts(updateLike);
    setTagsPosts(updateLike);

    try {
      const res = await fetchWithAuth(`/api/posts/${postId}/like`, { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        // Revert
        setPosts(updateLike);
        setFollowingPosts(updateLike);
        setFriendsPosts(updateLike);
        setCommunityPosts(updateLike);
        setTagsPosts(updateLike);
      }
    } catch {
      setPosts(updateLike);
      setFollowingPosts(updateLike);
      setFriendsPosts(updateLike);
      setCommunityPosts(updateLike);
      setTagsPosts(updateLike);
    }
  }, []);

  // Dislike post (optimistic update)
  const dislikePost = useCallback(async (postId: string) => {
    const updateDislike = (prev: FeedPost[]) =>
      prev.map(p => {
        if (p.id !== postId) return p;
        return { ...p, disliked: !p.disliked, dislikes: p.disliked ? (p.dislikes || 0) - 1 : (p.dislikes || 0) + 1 };
      });

    setPosts(updateDislike);
    setFollowingPosts(updateDislike);
    setFriendsPosts(updateDislike);
    setCommunityPosts(updateDislike);
    setTagsPosts(updateDislike);

    try {
      const res = await fetchWithAuth(`/api/posts/${postId}/dislike`, { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        setPosts(updateDislike);
        setFollowingPosts(updateDislike);
        setFriendsPosts(updateDislike);
        setCommunityPosts(updateDislike);
        setTagsPosts(updateDislike);
      }
    } catch {
      setPosts(updateDislike);
      setFollowingPosts(updateDislike);
      setFriendsPosts(updateDislike);
      setCommunityPosts(updateDislike);
      setTagsPosts(updateDislike);
    }
  }, []);

  // Delete post
  const deletePost = useCallback(async (postId: string) => {
    const removePost = (prev: FeedPost[]) => prev.filter(p => p.id !== postId);
    try {
      const res = await fetchWithAuth(`/api/posts/${postId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setPosts(removePost);
        setFollowingPosts(removePost);
        setFriendsPosts(removePost);
        setCommunityPosts(removePost);
        setTagsPosts(removePost);
      }
    } catch { /* silent */ }
  }, []);

  // Pin post
  const pinPost = useCallback(async (postId: string, isPinned: boolean) => {
    try {
      await fetchWithAuth(`/api/posts/${postId}/${isPinned ? 'unpin' : 'pin'}`, {
        method: 'POST',
        credentials: 'include',
      });
      const togglePin = (prev: FeedPost[]) =>
        prev.map(p => p.id === postId ? { ...p, isPinned: !isPinned } : p);
      setPosts(togglePin);
    } catch { /* silent */ }
  }, []);

  // Create post
  const createPost = useCallback(async (postData: {
    content: string;
    title?: string;
    tags?: string[];
    mediaUrl?: string;
    mediaPosition?: 'above' | 'below';
    aboveContent?: boolean;
  }) => {
    try {
      // Convert mediaPosition to aboveContent/belowContent for the backend
      const { mediaPosition, ...rest } = postData;
      const body = {
        ...rest,
        aboveContent: mediaPosition === 'above',
        belowContent: mediaPosition === 'below',
      };
      const response = await fetchWithAuth('/api/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const data = await response.json();
        const newPost = transformBackendPost(data.post || data);
        setPosts(prev => [newPost, ...prev]);
        return newPost;
      }
    } catch { /* silent */ }
    return null;
  }, []);

  // Like comment
  const likeComment = useCallback(async (commentId: string, postId: string) => {
    const updateCommentLike = (prev: FeedPost[]) =>
      prev.map(p => {
        if (p.id !== postId || !p.replies) return p;
        return {
          ...p,
          replies: p.replies.map(r => {
            if (r.id !== commentId) return r;
            return { ...r, liked: !r.liked, likes: r.liked ? r.likes - 1 : r.likes + 1 };
          }),
        };
      });

    setPosts(updateCommentLike);
    setFollowingPosts(updateCommentLike);
    setFriendsPosts(updateCommentLike);
    setCommunityPosts(updateCommentLike);
    setTagsPosts(updateCommentLike);

    try {
      await fetchWithAuth(`/api/posts/comments/${commentId}/like`, { method: 'POST', credentials: 'include' });
    } catch { /* silent */ }
  }, []);

  // Dislike comment
  const dislikeComment = useCallback(async (commentId: string, postId: string) => {
    const updateCommentDislike = (prev: FeedPost[]) =>
      prev.map(p => {
        if (p.id !== postId || !p.replies) return p;
        return {
          ...p,
          replies: p.replies.map(r => {
            if (r.id !== commentId) return r;
            return { ...r, disliked: !r.disliked, dislikes: r.disliked ? r.dislikes - 1 : r.dislikes + 1 };
          }),
        };
      });

    setPosts(updateCommentDislike);
    setFollowingPosts(updateCommentDislike);
    setFriendsPosts(updateCommentDislike);
    setCommunityPosts(updateCommentDislike);
    setTagsPosts(updateCommentDislike);

    try {
      await fetchWithAuth(`/api/posts/comments/${commentId}/dislike`, { method: 'POST', credentials: 'include' });
    } catch { /* silent */ }
  }, []);

  return {
    posts: getSortedPosts(),
    activeTab,
    setActiveTab,
    sortBy,
    setSortBy,
    isRefreshing,
    isLoading,
    refreshFeed,
    likePost,
    dislikePost,
    deletePost,
    pinPost,
    createPost,
    likeComment,
    dislikeComment,
  };
}
